import React from 'react';
import { PencilIcon, CheckIcon, TrashIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import type { ProductSummary } from '../../hooks/useProviderProducts';
import { productLabel, ProductPricePosition } from '../../lib/pricePosition';
import PricePositionBadge from './PricePositionBadge';
import AutoTagChips from './AutoTagChips';

interface ProductListItemProps {
  product: ProductSummary;
  onEdit: (productId: string) => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (sku: string) => void;
  /** Product is on the merchant's "removed from my store" list. */
  removed?: boolean;
  onRemove?: (sku: string) => void;
  onRestore?: (sku: string) => void;
  /** Supplier's own standing on this product; absent in the merchant view. */
  pricePosition?: ProductPricePosition;
  /** Automatic tags this product gets in the merchant's store; merchant view only. */
  autoTags?: string[];
  autoTagsActive?: boolean;
}

const ProductListItem: React.FC<ProductListItemProps> = ({ product, onEdit, selectable, selected, onToggleSelect, removed, onRemove, onRestore, pricePosition, autoTags, autoTagsActive }) => {
  const handleClick = () => {
    if (selectable && onToggleSelect) {
      onToggleSelect(product.sku);
    } else {
      onEdit(product.product_id);
    }
  };

  return (
    <div
      className={`border-b border-white/[0.08] hover:bg-white/[0.03] transition-colors duration-150 cursor-pointer ${
        selectable && !selected ? 'opacity-50' : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Selection checkbox */}
        {selectable && (
          <div className="flex-shrink-0">
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                selected
                  ? 'bg-orange-500 border-orange-500'
                  : 'bg-transparent border-white/30'
              }`}
            >
              {selected && <CheckIcon className="w-3 h-3 text-white" />}
            </div>
          </div>
        )}

        {/* Stock indicator */}
        <div className="flex-shrink-0">
          <div className={`w-2 h-2 rounded-full ${product.in_stock ? 'bg-green-500' : 'bg-red-500'}`} />
        </div>

        {/* Product Info - Responsive Grid */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center">
          {/* Title */}
          <div className="md:col-span-3 min-w-0">
            <h3 className="text-xs font-semibold text-white  truncate">
              {product.title}
            </h3>
            {removed && (
              <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/20 text-red-400">
                Removed from store
              </span>
            )}
            <div className="mt-0.5 empty:hidden">
              <PricePositionBadge label={productLabel(pricePosition)} />
            </div>
            <div className="mt-0.5 empty:hidden">
              <AutoTagChips tags={autoTags} active={autoTagsActive} />
            </div>
          </div>

          {/* SKU */}
          <div className="md:col-span-2 min-w-0">
            <span className="text-xs  text-white/70 truncate block">
              {product.sku}
            </span>
          </div>

          {/* Vendor */}
          <div className="md:col-span-2 min-w-0">
            <p className="text-xs text-white/50  truncate">
              {product.vendor}
            </p>
          </div>

          {/* Category */}
          <div className="md:col-span-2 min-w-0">
            {product.category && (
              <span className="text-xs text-white/35  truncate block">
                {product.category}
              </span>
            )}
          </div>

          {/* Variants */}
          <div className="md:col-span-1 text-center">
            <span className="text-xs text-white/35 ">
              {product.variant_count}
            </span>
          </div>

          {/* Price & Stock */}
          <div className="md:col-span-1 text-right">
            <div className="text-xs font-bold text-white ">
              {product.lowest_price !== null ? `€${product.lowest_price.toFixed(2)}` : 'N/A'}
            </div>
            <div className="text-xs text-white/50 ">
              {product.total_quantity}
            </div>
          </div>

          {/* Actions */}
          <div className="md:col-span-1 flex justify-end items-center gap-1">
            {/* Remove from store is deliberately separate from the supplier checkbox:
                unchecking only stops sourcing from THIS supplier, removing takes the
                product off the storefront entirely. */}
            {(onRemove || onRestore) && (
              removed ? (
                <button
                  className="p-1.5 hover:bg-white/[0.06] rounded transition-colors"
                  title="Restore to my store"
                  aria-label="Restore to my store"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore?.(product.sku);
                  }}
                >
                  <ArrowUturnLeftIcon className="w-3.5 h-3.5 text-emerald-400" />
                </button>
              ) : (
                <button
                  className="p-1.5 hover:bg-white/[0.06] rounded transition-colors"
                  title="Remove from my store (all suppliers)"
                  aria-label="Remove from my store"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.(product.sku);
                  }}
                >
                  <TrashIcon className="w-3.5 h-3.5 text-red-400/80" />
                </button>
              )
            )}
            <button
              className="p-1.5 hover:bg-white/[0.06] rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(product.product_id);
              }}
            >
              <PencilIcon className="w-3.5 h-3.5 text-orange-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListItem;
